import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './DigitalSignatureEditor.html?raw';
import lang from 'dojo/_base/lang';

import _StatefulSection from '../../moreCore/UI/_StatefulSection/_StatefulSection';
import _GlobalSelectEntityDialogsMixin from '../_loadingHelpers/_GlobalSelectEntityDialogsMixin';
import _uiFrameMixin from '../../moreCore/UI/Container/uiFrame';

import { TabContainer } from "../../moreCore/_loadingHelpers/Tabbing";

import GenericDialog from '../../moreCore/UI/GenericDialog/GenericDialog';
import { SignatureFile, SignatureEntity, SignatureMailbox, SignatureLanguage } from "./DigitalSignatureTypes";

import "../../moreCore/UI/ClipboardTextBox/ClipboardTextBox";
import { FileHelpers } from "../../more/Helpers";
import SignatoryInput from './Input/SignatoryInput';
import { ExpandableSection, ExpandableSectionMarkings } from "../../moreCore/UI/Container/ExpandableSectionTypes";
import SearchSelect from '../../moreCore/UI/SearchSelect/SearchSelect';
import ApproverInput from './Input/ApporverInput';
import { SelectEntityDialogTypes } from "../../moreCore/_loadingHelpers/SelectEntityDialog";
import HintBox from '../../moreCore/UI/HintBox/HintBox';
import CheckBox from '../../moreCore/UI/CheckBox/CheckBox';
import Button from '../../moreCore/UI/Button/Button';
import Edit from '../../moreCore/UI/Edit/Edit';
import DateTimeInput from '../../moreCore/UI/DateTimeInput/DateTimeInput';
import DateInput from '../../moreCore/UI/DateInput/DateInput';

class DigitalSignatureEditor extends compose(_StatefulSection, _uiFrameMixin, _GlobalSelectEntityDialogsMixin) {
    templateString: any = template;

    allowButtons: boolean = true;

    private inputFilename: MdsLax<InstanceType<typeof Edit>>;
    private inputSignatureRequestSent: MdsLax<InstanceType<typeof DateTimeInput>>;
    private inputStatusSignature: MdsLax<InstanceType<typeof Edit>>;
    private inputExpirationDate: MdsLax<InstanceType<typeof DateInput>>;

    private inputLanguageCode: MdsLax<InstanceType<typeof SearchSelect>>;
    private inputSender: MdsLax<InstanceType<typeof HintBox>>;
    private inputHasAnchor: MdsLax<InstanceType<typeof CheckBox>>;

    private expanderSigner: MdsLax<InstanceType<typeof ExpandableSection>>;
    private signerInputs: Array<MdsLax<InstanceType<typeof SignatoryInput>>> = [];

    private expanderApprover: MdsLax<InstanceType<typeof ExpandableSection>>;
    private approverInputs: Array<MdsLax<InstanceType<typeof ApproverInput>>> = [];

    private inputContainerSigner: HTMLDivElement;
    private inputContainerApprover: HTMLDivElement;

    private buttonContainerApprover: HTMLDivElement;
    private buttonCreateApprover: MdsLax<InstanceType<typeof Button>>;

    private buttonContainerSigner: HTMLDivElement;
    private buttonCreateSigner: MdsLax<InstanceType<typeof Button>>;

    private countApprovers = 0;
    private countSigners = 0;

    setup(entity?: SignatureEntity, tabContainer?: MdsLax<InstanceType<typeof TabContainer>>, node?: HTMLElement) {
        let heading;

        if (entity) {
            this.internalName = "DigitalSignature_Editor_" + entity.ID;
            if (entity.is_new) {
                this.setData(entity);
                heading = this.translate("DigitalSignatur_Heading_NewDigitalSignatureEditor");
            } else {
                heading = `${this.translate("DigitalSignature_Heading_DigitalSignatureEditor")}: ${entity.$Displayname}`;
                entity.is_new = false;
                this.setStartupState("ready");
                this._loadEntity(entity.ID);
            }

        } else {
            this.internalName = "DigitalSignature_Editor_New";
            let newEntity: any = {
                ID: "",
                $Displayname: "",
                Classname: "",
                ClassPrintname: "",
                is_new: true
            }
            this.setData(newEntity);
            heading = this.translate("DigitalSignature_Heading_NewDigitalSignatureEditor")
        }

        if (node) {
            this.show("node", heading, null, node);
            this.allowButtons = false;
        } else if (tabContainer) {
            this.show("tab", heading, tabContainer.name);
        }
        else {
            this.show("dlg", heading);
        }

        if (this.allowButtons) this.createDefaultButton("save", lang.hitch(this, this._onClickSave), false, "primary");
    }

    //LOAD
    private _loadEntity(entityId: string): void {
        this.StandbyStart();
        this._deactivateDataButtons(true, true);
        this.RequestManager.callfunction(
            "Webtool_API_DigitalSignature__DigitalSignatureEditor_LoadEntity",
            { ID: entityId },
            lang.hitch(this, this._onLoadEntityResponse),
            lang.hitch(this, this._onLoadEntityError));
    }

    private _onLoadEntityResponse(response: SignatureEntity): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        response.is_new = false;
        this.expanderApprover.setMarking(ExpandableSectionMarkings.empty);
        this.expanderSigner.setMarking(ExpandableSectionMarkings.empty);
        this.setData(response);
    }

    private _onLoadEntityError(error): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.error(error);
    }

    //SAVE
    private _onClickSave(): void {
        this.StandbyStart();
        this._deactivateDataButtons(true, true);

        let entityId = this.getAttributeValue("ID");
        let classname = this.getAttributeValue("Classname");
        let isNew = this.getAttributeValue("is_new");
        let changedData = this.getChangedData();
        this.RequestManager.callfunction(
            "Webtool_API_DigitalSignature__DigitalSignatureEditor_SaveEntity",
            { ID: entityId, Classname: classname, is_new: isNew, changedData: changedData },
            lang.hitch(this, this._onSaveResponse),
            lang.hitch(this, this._onSaveError));
    }

    private _onSaveResponse(response): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.debug(response);
        this.emit("changed");
        this.emit("saved");
        this.showToast("Editor_Success_Save", "fas fa-check");
        this._loadEntity(response.entityId);
    }

    private _onSaveError(error): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.error(error);
        this.showToast("Editor_Error_Save", "fas fa-warning-triangle");
    }

    //ROLLBACK
    private _onClickRollback(e: MouseEvent): void {
        this.rollback();
    }

    //RELOAD
    private _onClickReload(e: MouseEvent): void {
        let entity = this.getAttributeValue("___root");
        this._loadEntity(entity.ID);
    }

    //DELETE
    private _onClickDelete(e: MouseEvent): void {
        this.StandbyStart();
        this._deactivateDataButtons(true, true);
        let entityId = this.getAttributeValue("ID");
        let classname = this.getAttributeValue("Classname");
        this.RequestManager.callfunction(
            "Webtool_API_DigitalSignature__DigitalSignatureEditor_DeleteEntity",
            { ID: entityId, Classname: classname },
            lang.hitch(this, this._onDeleteResponse),
            lang.hitch(this, this._onDeleteError));
    }

    private _onDeleteResponse(response): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.debug(response);
        this.emit("changed");
        this.close();
        this.showToast("Editor_Success_Delete", "fas fa-check");
    }

    private _onDeleteError(error): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.error(error);
        this.showToast("Editor_Error_Delete", "fas fa-warning-triangle");
    }


    //BUTTONS
    private _deactivateDataButtons(deactivate: boolean, deactivateAll?: boolean): void {
        this.requestContextButtonDisable("save", deactivate);
        this.requestContextButtonDisable("rollback", deactivate);
        if (deactivateAll) {
            this.requestContextButtonDisable("delete", deactivate);
            this.requestContextButtonDisable("reload", deactivate);
        }
    }

    private _deactivateCustomButtons(deactivate: boolean): void {
        this.requestContextButtonDisable("createSignatureLinks", deactivate);
        this.requestContextButtonDisable("cancelSignatureObject", deactivate);
        this.requestContextButtonDisable("requestStatus", deactivate);
        this.requestContextButtonDisable("downloadFile", deactivate);
    }


    //CreateSignatureLinks
    private _onClickCreateSignatureLinks(evt: MouseEvent) {
        let entity = this.getAttributeValue("___root");
        this.RequestManager.callfunction("Webtool_API_DigitalSignature__DigitalSignatureEditor_CreateSignatureLinks", { ...entity }, lang.hitch(this, this._onCreateSignatureLinksResponse), lang.hitch(this, this._onCreateSignatureLinksError));
    }

    private _onCreateSignatureLinksResponse(response): void {
        console.debug(response);
    }

    private _onCreateSignatureLinksError(error): void {
        console.error(error);
    }

    //CancelSignatureProcess
    private _onClickCancelSignatureProcess(evt: MouseEvent): void {
        let entity = this.getAttributeValue("___root");
        this.RequestManager.callfunction("Webtool_API_DigitalSignature__DigitalSignatureEditor_CancelSignatureProcess", { ...entity }, lang.hitch(this, this._onCancelSignatureProcessResponse), lang.hitch(this, this._onCancelSignatureProcessError));
    }

    private _onCancelSignatureProcessResponse(response): void {
        console.debug(response);
    }

    private _onCancelSignatureProcessError(error): void {
        console.error(error);
    }

    //RequestStatus
    private _onClickRequestStatus(evt: MouseEvent): void {
        let entity = this.getAttributeValue("___root");
        this.RequestManager.callfunction("Webtool_API_DigitalSignature__DigitalSignatureEditor_RequestStatus", { ...entity }, lang.hitch(this, this._onRequestStatusResponse), lang.hitch(this, this._onRequestStatusError));
    }

    private _onRequestStatusResponse(response): void {
        console.debug(response);
    }

    private _onRequestStatusError(error): void {
        console.error(error);
    }

    //DownloadFile
    private _onClickDownloadFile(evt: MouseEvent): void {
        let entity = this.getAttributeValue("___root");
        this.RequestManager.callfunction("Webtool_API_DigitalSignature__DigitalSignatureEditor_DownloadFile", { ...entity }, lang.hitch(this, this._onDownloadFileResponse), lang.hitch(this, this._onDownloadFileError));
    }

    private _onDownloadFileResponse(response: {file: FileFinder.FileDescriptorWithBase64 | "no file"}): void {
        console.debug(response);

        if (response.file === "no file") {
            this.showToast("DigitalSignature_Error_NoFileCreated", "fas fa-exclamation-triangle");
        } else if (response.file) {
            FileHelpers.provideFile(response.file);
        }

    }

    private _onDownloadFileError(error): void {
        console.error(error);
    }

    //STATEFULSECTION
    protected onDirtyChange(newState: boolean) {
        this._deactivateDataButtons(!newState);
        this.setDirtyMarker(newState);
        this._deactivateCustomButtons(newState);
    }

    protected _onChanged(attribute: string, value: any) {
        switch (attribute) {
            case "is_document_with_anchors":
                this.disableFieldplacementButtons(value);
                break;
            case "ref_exchange_mailbox":
                this.setSenderInput(value);
                break;
        }

        let data = this.getChangedData();
        data["ID"] = this.getAttributeValue("ID");
        data["Classname"] = this.getAttributeValue("Classname");
        this.emit("changed", data);
    }

    protected afterSetData(): void {
        super.afterSetData.apply(this, arguments as any);

        this.destroyInputs();

        let isNew = this.getAttributeValue("is_new");
        if (!isNew) {
            if (this.allowButtons) this.createDefaultButton("delete", lang.hitch(this, this._onClickDelete), true, "secondary");
            if (this.allowButtons) this.createDefaultButton("rollback", lang.hitch(this, this._onClickRollback), false, "secondary");
            if (this.allowButtons) this.createDefaultButton("reload", lang.hitch(this, this._onClickReload), true, "primary");
            this.changeInternalName("DigitalSignatureEditor_Editor_" + this.getAttributeValue("ID"));

            this.addButtonEx({ internalName: "createSignatureLinks", callback: lang.hitch(this, this._onClickCreateSignatureLinks), label: "DigitalSignature_Label_CreateSignatureLinks", iconClass: "fas fa-link", position: "secondary" });
            this.addButtonEx({ internalName: "cancelSignatureObject", callback: lang.hitch(this, this._onClickCancelSignatureProcess), label: "DigitalSignature_Label_CancelSignatureProcess", iconClass: "fas fa-ban", position: "secondary" });
            this.addButtonEx({ internalName: "requestStatus", callback: lang.hitch(this, this._onClickRequestStatus), label: "DigitalSignature_Label_RequestStatus", iconClass: "fas fa-sync", position: "secondary" });
            this.addButtonEx({ internalName: "downloadFile", callback: lang.hitch(this, this._onClickDownloadFile), label: "DigitalSignature_Label_DownloadFile", iconClass: "fas fa-download", position: "secondary" });

            this.setTitles(`${this.translate("DigitalSignature_Heading_DigitalSignatureEditor")}: ${this.getAttributeValue("$Displayname")}`);
        } else {

        }
        this._deactivateDataButtons(true);

        let approver1 = this.getAttributeValue("ref_approver");
        let isManualApprover1 = this.getAttributeValue("is_manual_approver");
        if (approver1 || isManualApprover1) this.createApproverInput(1);
        let approver2 = this.getAttributeValue("ref_approver_2");
        let isManualApprover2 = this.getAttributeValue("is_manual_approver_2");
        if (approver2 || isManualApprover2) this.createApproverInput(2);

        let signer1 = this.getAttributeValue("ref_signatory");
        let isManualSigner1 = this.getAttributeValue("is_manual_signer");
        if (signer1 || isManualSigner1) this.createSignatoryInput(1);
        let signer2 = this.getAttributeValue("ref_signatory_2");
        let isManualSigner2 = this.getAttributeValue("is_manual_signer_2");
        if (signer2 || isManualSigner2) this.createSignatoryInput(2);
        let signer3 = this.getAttributeValue("ref_signatory_3");
        let isManualSigner3 = this.getAttributeValue("is_manual_signer_3");
        if (signer3 || isManualSigner3) this.createSignatoryInput(3);
        let signer4 = this.getAttributeValue("ref_signatory_4");
        let isManualSigner4 = this.getAttributeValue("is_manual_signer_4");
        if (signer4 || isManualSigner4) this.createSignatoryInput(4);

        this._reloadInputs();

        let language = this.getAttributeValue("ref_language");
        this._loadLanguageValues(language);

        let hasAnchor = this.getAttributeValue("is_document_with_anchors");
        this.disableFieldplacementButtons(hasAnchor);
        if (hasAnchor) this.disableNode(this.inputHasAnchor, true);

        this._handleContextButtonStates();
    }

    private destroyInputs() {
        for (let signatoryInput of this.signerInputs) {
            if (signatoryInput && !signatoryInput._destroyed) signatoryInput.destroy();
        }

        for (let approverInput of this.approverInputs) {
            if (approverInput && !approverInput._destroyed) approverInput.destroy();
        }

        this.signerInputs = [];
        this.approverInputs = [];

        this.UiModder.domConstruct.empty(this.inputContainerSigner);
        this.UiModder.domConstruct.empty(this.inputContainerApprover);
    }

    private _reloadInputs() {
        for (let tempInput of this.signerInputs) {
            tempInput.setData(this.getAttributeValue("___root"));
        }

        for (let tempInput of this.approverInputs) {
            tempInput.setData(this.getAttributeValue("___root"));
        }


    }

    private disableFieldplacementButtons(bDisable: boolean): void {
        for (let input of this.signerInputs) {
            input.disableFieldPlacmeentButton(bDisable);
        }
    }

    private setSenderInput(sender: SignatureMailbox): void {
        for (let input of this.signerInputs) {
            input.setAttributeValue("ref_exchange_mailbox", sender);
        }
    }

    private _handleContextButtonStates(): void {
        if (this.isReadonly()) {
            this.requestContextButtonRemove("createSignatureLinks");
            this.requestContextButtonRemove("cancelSignatureObject");
            this.requestContextButtonRemove("requestStatus");
            return
        }


        let signer1 = this.getAttributeValue("ref_signatory");
        let signatureEntityUrl1 = this.getAttributeValue("signature_entity_url");
        let signatureField1Set = this.getAttributeValue("signature_field_1_set");

        let signer2 = this.getAttributeValue("ref_signatory_2");
        let signatureEntityUrl2 = this.getAttributeValue("signature_entity_url_2");
        let signatureField2Set = this.getAttributeValue("signature_field_2_set");

        let signer3 = this.getAttributeValue("ref_signatory_3");
        let signatureEntityUrl3 = this.getAttributeValue("signature_entity_url_3");
        let signatureField3Set = this.getAttributeValue("signature_field_3_set");

        let signer4 = this.getAttributeValue("ref_signatory_4");
        let signatureEntityUrl4 = this.getAttributeValue("signature_entity_url_4");
        let signatureField4Set = this.getAttributeValue("signature_field_4_set");

        let refStatusSignatureEntity = this.getAttributeValue("ref_status_signature_entity");
        let signRequestID = this.getAttributeValue("signrequest_id");


        let enabled = true;

        if (signer1 && (!signer1?.ref_vw_telecom_addresses?.emailaddress || signatureEntityUrl1 || !signatureField1Set)) enabled = false;
        if (signer2 && (!signer2?.ref_vw_telecom_addresses?.emailaddress || signatureEntityUrl2 || !signatureField2Set)) enabled = false;
        if (signer3 && (!signer3?.ref_vw_telecom_addresses?.emailaddress || signatureEntityUrl3 || !signatureField3Set)) enabled = false;
        if (signer4 && (!signer4?.ref_vw_telecom_addresses?.emailaddress || signatureEntityUrl4 || !signatureField4Set)) enabled = false;
        if (!signer1 && !signer2 && !signer3 && !signer4) enabled = false;


        this.requestContextButtonDisable("createSignatureLinks", !enabled || this.isReadonly());


        enabled = true;

        if (!refStatusSignatureEntity?.is_cancel_allowed || !signRequestID) enabled = false;

        this.requestContextButtonDisable("cancelSignatureObject", !enabled || this.isReadonly());

        this.requestContextButtonDisable("requestStatus", this.isReadonly());
    }

    //UIFRAMEMIXIN
    //UIFRAMEMIXIN
    _onCloseRequest() {
        let hasChanged = this.isDirty() === true;

        if (hasChanged) {
            let dlgConfirm = new GenericDialog();
            dlgConfirm.show(this.translate("DigitalSignature_DigitalSignatureEditor_AskSavePendingChanges"),
                this.translate("General_PendingChanges") + " - " + this.title,
                "",
                "",
                "yesnocancel",
                lang.hitch(this, this._confirmCallback));

            return false;

        } else {
            return true;
        }
    }

    private _confirmCallback(result) {
        if (result == -1) { // nein
            this.emit("notSaved");
            this.close();
        } else if (result == 0) { // cancel
            this.emit("canceled");
        }
        else {
            this._onClickSave();
            this.on("saved", lang.hitch(this, this.close));
        }
    }

    private _onClickCreateSignatoryInput() {
        let amountInputs = this.signerInputs.length;
        this.createSignatoryInput(amountInputs + 1);
    }

    private createSignatoryInput(inputNumber: number): void {
        let amountInputs = this.signerInputs.length;

        if (amountInputs >= 4) return;
        amountInputs++;
        if (amountInputs < inputNumber) this.createSignatoryInput(inputNumber - 1);
        let input = new SignatoryInput(inputNumber);
        this.signerInputs.push(input);
        input.placeAt(this.inputContainerSigner);
        input.setStartupState("ready");

        let data = this.getAttributeValue("___root");
        input.setData(data);
        input.on("changed", lang.hitch(this, this._onChangedInput));

        if (amountInputs < 4) this.UiModder.domConstruct.create("hr", { "class": "signatoryInputRule" }, this.inputContainerSigner);
        if (amountInputs >= 4) this.hideNode(this.buttonContainerSigner);
        this.expanderSigner.setMarking(ExpandableSectionMarkings.hasContent);

        if (this.isReadonly()) input.setReadonly(this.isReadonly());
    }


    private _onClickCreateApproverInput() {
        let amountInputs = this.approverInputs.length;
        this.createApproverInput(amountInputs + 1);
    }

    private createApproverInput(inputNumber: number): void {
        let amountInputs = this.approverInputs.length;

        if (amountInputs >= 2) return;
        amountInputs++;
        if (amountInputs < inputNumber) this.createApproverInput(inputNumber - 1);
        let input = new ApproverInput(inputNumber);
        this.approverInputs.push(input);
        input.placeAt(this.inputContainerApprover);

        let data = this.getAttributeValue("___root");
        input.setData(data);
        input.setStartupState("ready");
        input.on("changed", lang.hitch(this, this._onChangedInput));

        if (amountInputs < 2) this.UiModder.domConstruct.create("hr", { "class": "signatoryInputRule" }, this.inputContainerApprover);
        if (amountInputs >= 2) this.hideNode(this.buttonContainerApprover);

        this.expanderApprover.setMarking(ExpandableSectionMarkings.hasContent);

        if (this.isReadonly()) input.setReadonly(this.isReadonly());

    }

    private _onChangedInput(changedData: Object): void {
        for (let property of Object.getOwnPropertyNames(changedData)) {
            var value = changedData[property];
            this.setAttributeValue(property, value);
        }
    }

    private _loadLanguageValues(currentValue): void {
        this.RequestManager.callfunction("Webtool_API_DigitalSignature__DigitalSignatureEditor_GetLanguageCodes", {}, lang.hitch(this, this._onLoadLanguageValuesResponse, currentValue), lang.hitch(this, this._onLoadLanguageValuesError));
    }

    private _onLoadLanguageValuesResponse(currentValue: SignatureLanguage, response: Array<SignatureLanguage>): void {
        this.inputLanguageCode.setData(currentValue, response, true);
    }

    private _onLoadLanguageValuesError(error): void {
        console.error(error);
    }

    private _onClickSED(mode: string, evt: MouseEvent): void {
        this.showSelectEntityDialog(evt, mode, lang.hitch(this, this._onSEDCallback));
    }

    private _onSEDCallback(mode: string, data: Array<string>, items: Array<SelectEntityDialogTypes.Item<SignatureMailbox>>): void {
        if (!items || items.length === 0) return;

        let entity;
        switch (mode) {
            case "selectMailbox":
                entity = items[0].entry.data;
                this.setAttributeValue("ref_exchange_mailbox", entity);
                break;
        }
    }

    setReadonly(readonly?: "initial" | boolean): void {
        super.setReadonly.apply(this, arguments as any);

        this.inputFilename.setReadonly(true);
        this.inputStatusSignature.setReadonly(true);
        this.inputSignatureRequestSent.setReadonly(true);
        this.inputExpirationDate.setReadonly(true);


        for (let input of this.approverInputs) {
            input.setReadonly(this.isReadonly());
        }
        for (let input of this.signerInputs) {
            input.setReadonly(this.isReadonly());
        }

        this.buttonCreateApprover.setReadonly(this.isReadonly());
        this.buttonCreateSigner.setReadonly(this.isReadonly());
        this.buttonCreateApprover.destroy();
        this.buttonCreateSigner.destroy();

        this._handleContextButtonStates();
    }

    //WIDGETBASE
    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this.SEDconfigCallfunction = "Webtool_API_DigitalSignature__DigitalSignatureEditor_GetSEDConfig";

        this.inputSender.addInlineButton(lang.hitch(this, this._onClickSED, "selectMailbox"));
    }

    startup(): void {
        super.startup.apply(this, arguments as any);
    }
}

export default mdsWidget(DigitalSignatureEditor);